'use strict';
/* ==========================================================================
   levels/title.js — the title: a cut-and-paste ZINE COVER pinned to the corkboard, with a CASE-BOARD touch (DESIGN.md
   §2). Copied from lessons/simile/levels/title.js. A photocopied cover page (toner speckle, two staples down the spine),
   the lesson name as ransom letters in the zine palette (black / white / neon), a neon highlighter swipe under the second
   line, a linocut eye (the POV "camera") as the logo, "issue #2" typed, evidence tags (the clue colours of
   core-local/narration.js) pinned round the edge with red string between two of them, Nia (left) and Ms. Park (right)
   rising in, ▶ Play → the map. On a phone held upright the controls move to the portrait dock (Dock.title) and the stage
   keeps only the art.
   Levels.title.loading() draws the "warming up the copier…" screen while assets load (no textures yet).
   Conventions (shared/engine/v2/README.md): draw through g, a pure function of time (T, this.enteredAt), tappable
   things through UI.button / UI.tap, no per-frame shadowOn() (core-local/zine.js pieces are cached).
   ========================================================================== */
(function () {
  const PLAY = { x: 960, y: 986 };
  const Z = () => window.Zine;
  // the cover's ransom scraps: photocopy black / white + the neon highlighters (one constant array: letters cache per palette)
  const COVER_PAL = [['#141210', '#f7f5ef'], ['#f7f5ef', '#141210'], ['#fff04a', '#141210'], ['news', '#141210'], ['#ff6fbf', '#141210'], ['#141210', '#fff04a'], ['#72f06e', '#141210']];

  function titleLines(text) {
    const s = String(text).toUpperCase(), i = s.indexOf(',');
    if (i > 0) return [s.slice(0, i + 1), s.slice(i + 1).trim()];
    const words = s.split(/\s+/); const k = Math.ceil(words.length / 2);
    return [words.slice(0, k).join(' '), words.slice(k).join(' ')];
  }
  const fitSize = (line, seed, maxW, max) => { const w = ransom(line, -5000, -5000, 100, 1e9, { seed, palette: COVER_PAL }); return Math.min(max, (maxW / Math.max(1, w)) * 100); };

  // the evidence tags pinned round the cover (a taste of the case files); red string joins the first two
  const CHIPS = [
    { id: 'says-vs-does', x: 590, y: 770, rot: -0.06 }, { id: 'others-react', x: 900, y: 805, rot: 0.04 },
    { id: 'missing-info', x: 1330, y: 775, rot: 0.05 },
  ];

  class Title extends Scene {
    enter() { this.started = false; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a paper tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {}, Zn = Z();
      Zn.cork();

      // the cover: a photocopied A5 page, stapled down the spine, taped at the corners
      const cs = pop(T, t0 + 0.05, 0.45, 1.6);
      Zn.page(960, 470, 1100, 820, { seed: 17, rot: -0.012, s: cs, staple: 'left', tape: 2, dark: 0.55 });
      if (cs >= 0.999) {
        Zn.type('ISSUE #2', 500, 118, 34, { t0: t0 + 0.3, cps: 24 });
        Zn.type('free · take one', 1420, 118, 30, { t0: t0 + 0.5, cps: 24, align: 'right', color: Zn.C.grey });
        // the title: ransom letters, two lines; neon swipe under the second
        const lines = titleLines(L.title || 'Who\'s Telling This?');
        const sizes = lines.map((l, i) => fitSize(l, 401 + i, 900, 124));
        const y1 = 290, y2 = y1 + sizes[0] * 1.32;
        const w2 = ransom(lines[1] || '', -5000, -5000, sizes[1] || 100, 1e9, { seed: 402, palette: COVER_PAL });
        Zn.highlight(960 - w2 / 2 - 24, y2 + 6, w2 + 60, sizes[1] * 1.25, '#fff04a', { t0: t0 + 1.25, dur: 0.45, seed: 9, rot: -0.01 });
        lines.forEach((l, i) => ransom(l, 960, i ? y2 : y1, sizes[i], t0 + 0.4 + i * 0.45, { seed: 401 + i, palette: COVER_PAL }));
        // subtitle, typed, with a linocut eye (the POV camera) as the zine's logo; it blinks now and then
        Zn.type(L.subtitle || '', 960, y2 + sizes[1] * 1.22, 40, { t0: t0 + 1.5, cps: 40, align: 'center' });
        const blink = ((T - t0) % 4.2) > 4.05 ? 0.1 : 1;
        Zn.eye(1405, 186, 52, { seed: 31, open: E.outBack(P(T, t0 + 1.7, t0 + 2.1), 1.4) * blink });
        hand('a zine about who\'s really talking', 960, y2 + sizes[1] * 1.22 + 74, 44, t0 + 1.9, { color: Zn.C.red, rot: -0.02, seed: 44 });
      }
      CHIPS.forEach((c, i) => Zn.label(c.id, c.x, c.y, { size: 30, rot: c.rot, seed: 60 + i, s: pop(T, t0 + 1.2 + i * 0.12, 0.35, 2.4) }));
      Zn.string(CHIPS[0].x + 120, CHIPS[0].y - 18, CHIPS[1].x - 110, CHIPS[1].y - 20, { t0: t0 + 1.7, dur: 0.5, sag: 40 });

      // Nia (left) and Ms. Park (right) rise in from below
      const riseN = E.outBack(P(T, t0 + 0.5, t0 + 1.0), 1.4), riseP = E.outBack(P(T, t0 + 0.7, t0 + 1.2), 1.4);
      Puppet.draw('nia', 230, 1080 + (1 - riseN) * 600, 640, { facing: 1, t: T });
      Puppet.draw('park', 1690, 1080 + (1 - riseP) * 600, 720, { facing: -1, t: T });

      if (UI.docked()) { Dock.title({ onStart: () => this.go(), onContinue: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 380, h: 116, color: '#fff04a', text: Zn.C.ink, font: "'Luckiest Guy'", size: 64, rot: -0.015 })) this.go();
      if (t > 2.4) hand(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x + 330 + (UI.tap(116) - 116) / 2, PLAY.y + 6, UI.text(34), t0 + 2.4, { color: 'rgba(20,18,16,0.75)', seed: 61, align: 'left' });
    }
  }
  Title.PLAY = PLAY;                         // tools/integration.js clicks it

  /* loading screen: no textures yet → plain photocopy grey, a copier light sweeping across */
  Title.loading = function () {
    g.fillStyle = '#e9e6de'; g.fillRect(0, 0, Game.W, Game.H);
    const u = (T * 0.6) % 1, x = 260 + u * 1400;
    const gr = g.createLinearGradient(x - 120, 0, x + 120, 0);
    gr.addColorStop(0, 'rgba(120,255,190,0)'); gr.addColorStop(0.5, 'rgba(120,255,190,0.55)'); gr.addColorStop(1, 'rgba(120,255,190,0)');
    g.fillStyle = gr; g.fillRect(x - 120, 360, 240, 360);
    g.strokeStyle = '#141210'; g.lineWidth = 6; g.strokeRect(260, 360, 1400, 360);
    g.save(); g.font = "64px 'Special Elite', 'Courier New', monospace"; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = '#141210';
    g.fillText('warming up the copier…', 960, 820); g.restore();
  };

  Levels.title = Title;
})();
